import {
    ArrowRight,
    Bell,
    BrainCircuit,
    Gauge,
    Loader2,
    Microscope,
    Minus,
    ShieldAlert,
    Sparkles,
    Target,
    TriangleAlert,
    X,
    Zap,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from '@tanstack/react-router'
import { getStrikeStepForSymbol } from '../config/constants'
import { useDashboardData } from '../hooks/useDashboardData'
import { useMarketStore } from '../services/store'
import { useSettingsStore } from '../stores/settingsStore'
import { getSortedOptions } from '../lib/analysis-utils'
import { calculateTradeIntelligence, type TradeIntelligenceInput } from '../lib/trade-intelligence'
import type { AnalysisMode, AnalysisResult, MarketData, MarketSnapshot } from '../types'
import { useStrikeIndicators } from '../hooks/useStrikeIndicators'
import { useAIInsights } from '../hooks/useAIInsights'
import { AiInsightsModal } from './ai/AiInsightsModal'
import { Sparkles as SparklesIcon, BrainCircuit as BrainIcon } from 'lucide-react'

interface TradeIntelAnalyticsProps {
    mode: AnalysisMode
    range: number
    popupOnly?: boolean
    suppressPopup?: boolean
    history?: MarketSnapshot[] // For trend analysis
    rsiData?: { ceRsi: number; peRsi: number } // For RSI divergence
    marketData?: MarketData
    analysis?: AnalysisResult
    selectedCeStrike?: number
    selectedPeStrike?: number
    signal?: any // Optional pre-computed signal to sync with parent
}

export const TradeIntelAnalytics: React.FC<TradeIntelAnalyticsProps> = ({
    mode,
    range,
    popupOnly = false,
    suppressPopup = false,
    history = [],
    rsiData,
    marketData: propMarketData,
    analysis: propAnalysis,
    selectedCeStrike: propCeStrike,
    selectedPeStrike: propPeStrike,
    signal: propSignal,
}) => {
    const { marketData: hookMarketData, analysis: hookAnalysis } = useDashboardData()

    // Use props if available (for time travel), otherwise fallback to hook (live)
    const marketData = propMarketData || hookMarketData
    const analysis = propAnalysis || hookAnalysis

    // const { userSelections } = useMarketData();
    // const { userSelections } = useMarketData();
    // const [ computedSignal, setComputedSignal ] = useState<any>( null ); // Moved to useMemo

    const symbol = marketData?.symbol || 'NIFTY'

    const effectiveCeStrike = useMemo(() => {
        if (propCeStrike && propCeStrike > 0) return propCeStrike
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${symbol}_ce_strike`)
            if (saved && saved !== '0' && saved !== 'undefined' && saved !== 'null') {
                const parsed = parseInt(saved, 10)
                if (!isNaN(parsed) && parsed > 0) return parsed
            }
        }
        return analysis?.resistanceStrike || 0
    }, [propCeStrike, analysis?.resistanceStrike, symbol, marketData]) // trigger on marketData to pick up updates

    const effectivePeStrike = useMemo(() => {
        if (propPeStrike && propPeStrike > 0) return propPeStrike
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${symbol}_pe_strike`)
            if (saved && saved !== '0' && saved !== 'undefined' && saved !== 'null') {
                const parsed = parseInt(saved, 10)
                if (!isNaN(parsed) && parsed > 0) return parsed
            }
        }
        return analysis?.supportStrike || 0
    }, [propPeStrike, analysis?.supportStrike, symbol, marketData])

    const historicalDate = useSettingsStore(s => s.historicalDate)

    const { ceIndicators, peIndicators } = useStrikeIndicators({
        selectedCeStrike: effectiveCeStrike,
        selectedPeStrike: effectivePeStrike,
        activeMarketData: marketData || null,
        symbol: marketData?.symbol || 'NIFTY',
        historicalDate,
    })

    // Popup Setup -> Sync to Global Store
    const prevSetupRef = useRef<any>(null)
    const setActiveTradeAlert = useMarketStore((s) => s.setActiveTradeAlert)
    const setTradeAlertVisible = useMarketStore((s) => s.setTradeAlertVisible)

    const effectiveRsiData = useMemo(() => {
        // If passed as prop from Inspector and it's valid, use it
        if (rsiData && (rsiData.ceRsi !== 50 || rsiData.peRsi !== 50)) return rsiData
        
        // Fallback to globally synchronized RSI from local storage (if Inspector ran)
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${symbol}_rsi`)
            if (saved) {
                try {
                    return JSON.parse(saved)
                } catch (e) {
                    // ignore
                }
            }
        }
        
        // Final fallback
        return rsiData || { ceRsi: 50, peRsi: 50 }
    }, [rsiData, symbol, marketData]) // trigger on marketData

    // --- ALGO ENGINE V4.0 (Enhanced) ---
    const computedSignal = useMemo(() => {
        if (propSignal) return propSignal

        // Build the input object for the new API
        const input: TradeIntelligenceInput = {
            marketData: marketData!,
            analysis: analysis
                ? {
                      ...analysis,
                      variance: analysis.variance || {
                          totalCount: 0,
                          ceCount: 0,
                          peCount: 0,
                          finalVariance: 0,
                          buyCount: 0,
                          sellCount: 0,
                          ceVolCount: 0,
                          peVolCount: 0,
                          maxStrike: 0,
                          minStrike: 0,
                          maxDiff: 0,
                          minDiff: 0,
                      },
                  }
                : (null as any),
            mode,
            history,
            rsiData: effectiveRsiData,
            ceIndicators,
            peIndicators,
            ceStrikeLtp: marketData?.options?.find((o: any) => o.strike === effectiveCeStrike)?.callPrice || 0,
            peStrikeLtp: marketData?.options?.find((o: any) => o.strike === effectivePeStrike)?.putPrice || 0,
        }
        return calculateTradeIntelligence(input)
    }, [
        propSignal,
        marketData,
        analysis,
        mode,
        history,
        effectiveRsiData,
        ceIndicators,
        peIndicators,
        effectiveCeStrike,
        effectivePeStrike,
    ])

    const {
        isOpen: isAiModalOpen,
        response: aiResponse,
        isGenerating: isAiGenerating,
        generateInsights,
        closeAiModal,
    } = useAIInsights()

    const handleAiIdeasClick = () => {
        if (!marketData || !analysis || !computedSignal) return

        const settings = {
            symbol: marketData.symbol || 'NIFTY',
            range: range,
            mode: mode,
        }

        generateInsights(
            analysis,
            marketData,
            settings as any,
            'StrikeInspector', // Context for Inspector page
            {
                selectedStrikes: [effectiveCeStrike, effectivePeStrike],
                activeStrategy: computedSignal.setup,
            },
            history,
            computedSignal.thesisPoints?.map((tp: any) => ({ type: tp.type, message: tp.text, confidence: 'High' })),
        )
    }

    // Dynamic Router Logic to prevent racing between global root worker vs active page workers
    const location = useLocation()
    const isSpecializedPage = location.pathname.includes('/inspector') || location.pathname.includes('/scanner')
    
    // Only update popup if:
    // 1. Not manually suppressed via props (suppressPopup)
    // 2. Either we are a specialized page local worker (popupOnly=false) OR we are not currently viewing a specialized page (e.g. Dashboard)
    const activelyManagePopup = !suppressPopup && (!popupOnly || !isSpecializedPage)

    useEffect(() => {
        if (!computedSignal || !computedSignal.setup) return
        if (!activelyManagePopup) return // Yield popup control

        const { setup } = computedSignal

        // Check for Changes and Trigger Popup
        const prev = prevSetupRef.current
        if (
            setup &&
            (!prev ||
                prev.type !== setup.type ||
                prev.strike !== setup.strike ||
                prev.entry !== setup.entry ||
                prev.sl !== setup.sl ||
                prev.target !== setup.target ||
                prev.header !== setup.header)
        ) {
            // Change Detected -> Update Global Store
            setActiveTradeAlert({ setup, warning: computedSignal.management?.warning })
            setTradeAlertVisible(true)
            prevSetupRef.current = setup
        }
    }, [computedSignal]) // Only effect for Side Effects (Alerts)

    if (!marketData || !computedSignal) return null

    const { signalType, signalStrength, setup, levels, thesisPoints, metrics, trendFactors } = computedSignal
    const isBull = signalType === 'BULLISH'
    const isBear = signalType === 'BEARISH'
    const isWaiting = setup.type === 'WAITING'

    // COLOR THEMES V3
    const theme = isBull
        ? {
              main: 'emerald',
              bg: 'bg-emerald-500',
              subBg: 'bg-emerald-50 dark:bg-emerald-900/20',
              text: 'text-emerald-600 dark:text-emerald-400',
              textDark: 'text-emerald-900 dark:text-emerald-100',
              border: 'border-emerald-200 dark:border-emerald-800',
              gradient: 'from-emerald-500 via-green-400 to-teal-500',
              shadow: 'shadow-emerald-500/20 dark:shadow-emerald-900/20',
              accent: 'text-teal-500 dark:text-teal-400',
          }
        : isBear
          ? {
                main: 'rose',
                bg: 'bg-rose-500',
                subBg: 'bg-rose-50 dark:bg-rose-900/20',
                text: 'text-rose-600 dark:text-rose-400',
                textDark: 'text-rose-900 dark:text-rose-100',
                border: 'border-rose-200 dark:border-rose-800',
                gradient: 'from-rose-500 via-red-400 to-pink-500',
                shadow: 'shadow-rose-500/20 dark:shadow-rose-900/20',
                accent: 'text-pink-500 dark:text-pink-400',
            }
          : isWaiting
            ? {
                  main: 'slate',
                  bg: 'bg-slate-500',
                  subBg: 'bg-slate-100 dark:bg-slate-800',
                  text: 'text-slate-600 dark:text-slate-400',
                  textDark: 'text-slate-800 dark:text-slate-200',
                  border: 'border-slate-200 dark:border-slate-700',
                  gradient: 'from-slate-400 via-gray-500 to-zinc-500',
                  shadow: 'shadow-slate-500/10 dark:shadow-black/20',
                  accent: 'text-slate-500 dark:text-slate-400',
              }
            : {
                  main: 'indigo',
                  bg: 'bg-indigo-500',
                  subBg: 'bg-indigo-50 dark:bg-indigo-900/20',
                  text: 'text-indigo-600 dark:text-indigo-400',
                  textDark: 'text-indigo-900 dark:text-indigo-100',
                  border: 'border-indigo-200 dark:border-indigo-800',
                  gradient: 'from-indigo-400 via-blue-500 to-violet-500',
                  shadow: 'shadow-indigo-500/20 dark:shadow-indigo-900/20',
                  accent: 'text-violet-500 dark:text-violet-400',
              }

    const management = computedSignal.management // Quantum V5.0 Management

    return (
        <>
            {!popupOnly && (
                <div className="w-full mb-6 perspective-1000 animate-in fade-in slide-in-from-bottom-8 duration-1000">
                    {/* MAIN CONTAINER V3 - Ghost Design Refinements */}
                    <div
                        className={`relative rounded-3xl premium-card shadow-2xl ${theme.shadow} border-t-0 overflow-hidden`}
                    >
                        {/* AMBIENT GLOWS */}
                        <div
                            className={`absolute -top-20 -right-20 w-96 h-96 bg-linear-to-br ${theme.gradient} rounded-full blur-[100px] opacity-10 animate-pulse-slow`}
                        />
                        <div
                            className={`absolute -bottom-20 -left-20 w-80 h-80 ${theme.bg} rounded-full blur-[80px] opacity-5 pointer-events-none`}
                        />

                        <div className="grid grid-cols-1 min-h-[450px]">
                            {/* --- TOP PANEL: EXECUTION & SIGNAL --- */}
                            <div className="p-4 md:p-6 flex flex-col justify-between relative z-10 box-border border-b border-white/30 dark:border-slate-700/30">
                                {/* 1. Header Row */}
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-4">
                                        {/* Icon Badge */}
                                        {/* Icon Badge */}
                                        <div
                                            className={`p-3.5 rounded-2xl shadow-lg ring-1 ring-white/50 dark:ring-white/10 ${theme.subBg} ${theme.textDark}`}
                                        >
                                            {setup.strategyClass === 'TRAP' ? (
                                                <TriangleAlert
                                                    size={28}
                                                    strokeWidth={2.5}
                                                    className="text-rose-600 dark:text-rose-400 animate-pulse"
                                                />
                                            ) : isWaiting ? (
                                                <Loader2
                                                    size={28}
                                                    strokeWidth={2.5}
                                                    className="animate-spin text-slate-400 dark:text-slate-500"
                                                />
                                            ) : (
                                                <BrainCircuit size={28} strokeWidth={2.5} />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                                                <Sparkles size={10} className="text-amber-400" />
                                                Quantum Intelligence V5.0
                                            </h3>
                                            <h1 className="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tighter mt-0.5">
                                                {setup.header}
                                            </h1>
                                            {management?.warning && (
                                                <div className="flex items-center gap-1.5 mt-1 animate-pulse">
                                                    <ShieldAlert
                                                        size={12}
                                                        className={
                                                            setup.strategyClass === 'TRAP'
                                                                ? 'text-rose-500'
                                                                : 'text-amber-500'
                                                        }
                                                    />
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wide ${setup.strategyClass === 'TRAP' ? 'text-rose-600' : 'text-amber-600'}`}
                                                    >
                                                        {management.warning}
                                                    </span>
                                                </div>
                                            )}
                                            <button
                                                onClick={handleAiIdeasClick}
                                                className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-linear-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-200/50 dark:border-amber-700/50 text-amber-700 dark:text-amber-400 text-[10px] font-black uppercase tracking-widest transition-all hover:scale-105 active:scale-95 shadow-sm"
                                            >
                                                <SparklesIcon size={12} className="animate-pulse" />
                                                AI Trade Ideas
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confidence Pill */}
                                    <div className="flex flex-col items-end">
                                        <span
                                            className={`text-[9px] font-black uppercase tracking-widest mb-1 ${theme.accent}`}
                                        >
                                            Probability
                                        </span>
                                        <div className="flex items-baseline gap-1">
                                            <span
                                                className={`text-5xl font-black text-transparent bg-clip-text premium-cell-mono bg-linear-to-tr ${theme.gradient}`}
                                            >
                                                {computedSignal.setup.conviction}
                                            </span>
                                            <span className="text-xl font-bold text-slate-300 dark:text-slate-600">
                                                %
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. Trade Execution Card */}
                                <div className="bg-white/40 dark:bg-slate-800/40 backdrop-blur-md rounded-3xl p-6 border border-white/40 dark:border-slate-700/40 shadow-lg relative overflow-hidden group mb-4 ring-1 ring-white/60 dark:ring-slate-700/60">
                                    <div className={`absolute left-0 top-0 w-1.5 h-full ${theme.bg}`} />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                                        {/* Action Side */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span
                                                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white ${theme.bg} shadow-sm`}
                                                >
                                                    Recommended Setup
                                                </span>
                                                <span className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-wider">
                                                    • {setup.strategyClass}
                                                </span>
                                            </div>
                                            <div className="text-4xl md:text-5xl font-black tracking-tighter text-slate-800 dark:text-slate-100 flex items-center gap-3">
                                                {setup.type}
                                            </div>
                                            <div className="mt-3 flex items-center gap-3">
                                                <div className="px-3 py-1.5 bg-white/60 dark:bg-slate-700/60 rounded-lg text-sm premium-cell-mono font-bold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                                                    {setup.strike}
                                                </div>
                                                <ArrowRight size={18} className="text-slate-300 dark:text-slate-500" />
                                                <div className="text-sm font-bold text-slate-600 dark:text-slate-400">
                                                    Zone:{' '}
                                                    <span className="text-slate-900 dark:text-slate-200 border-b-2 border-slate-200 dark:border-slate-600">
                                                        {setup.entry}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Targets Grid */}
                                        <div className="grid grid-cols-2 gap-3">
                                            {/* Stop Loss */}
                                            <div className="flex flex-col p-3 rounded-2xl bg-rose-50/50 dark:bg-rose-900/20 border border-rose-100/50 dark:border-rose-800/50 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors">
                                                <div className="flex items-center gap-1.5 mb-1 opacity-60">
                                                    <ShieldAlert
                                                        size={14}
                                                        className="text-rose-600 dark:text-rose-400"
                                                    />
                                                    <span className="text-[10px] font-bold text-rose-800 dark:text-rose-300 uppercase">
                                                        Stop Loss
                                                    </span>
                                                </div>
                                                <span className="text-xl font-black text-rose-700 dark:text-rose-400 premium-cell-mono">
                                                    {setup.sl}
                                                </span>
                                            </div>
                                            {/* Target */}
                                            <div className="flex flex-col p-3 rounded-2xl bg-emerald-50/50 dark:bg-emerald-900/20 border border-emerald-100/50 dark:border-emerald-800/50 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors">
                                                <div className="flex items-center gap-1.5 mb-1 opacity-60">
                                                    <Target
                                                        size={14}
                                                        className="text-emerald-600 dark:text-emerald-400"
                                                    />
                                                    <span className="text-[10px] font-bold text-emerald-800 dark:text-emerald-300 uppercase">
                                                        Target
                                                    </span>
                                                </div>
                                                <div className="flex items-baseline gap-1.5">
                                                    <span className="text-xl font-black text-emerald-700 dark:text-emerald-400 premium-cell-mono">
                                                        {setup.target}
                                                    </span>
                                                    <span className="text-[10px] font-bold text-emerald-500 dark:text-emerald-400">
                                                        ({setup.upside})
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- BOTTOM PANEL: THESIS & DNA --- */}
                            <div className="bg-white/20 dark:bg-slate-900/20 backdrop-blur-md p-4 md:p-6 flex flex-col relative z-20">
                                {/* 1. Thesis Stream */}
                                <div className="mb-4 flex-1">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Microscope size={16} className="text-indigo-500 dark:text-indigo-400" />
                                        <h4 className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                                            Trade Thesis
                                        </h4>
                                    </div>

                                    {/* Holistic Narrative Summary */}
                                    {computedSignal.signalType !== 'NEUTRAL' && (
                                        <div className="mb-5 text-sm font-medium text-slate-700 dark:text-slate-300 leading-relaxed bg-white/60 dark:bg-slate-800/60 p-4 rounded-xl border border-white/60 dark:border-slate-700/60 shadow-sm ring-1 ring-slate-900/5 dark:ring-white/5">
                                            <p>
                                                The market is currently showing a{' '}
                                                <strong
                                                    className={
                                                        computedSignal.signalType === 'BULLISH'
                                                            ? 'text-emerald-600 dark:text-emerald-400'
                                                            : 'text-rose-600 dark:text-rose-400'
                                                    }
                                                >
                                                    {computedSignal.signalStrength.toLowerCase()}{' '}
                                                    {computedSignal.signalType.toLowerCase()}
                                                </strong>{' '}
                                                bias.{' '}
                                                {computedSignal.setup.strategyClass.includes('TRAP') ||
                                                computedSignal.setup.strategyClass.includes('SWEEP')
                                                    ? `However, a structural trap has been detected: the system is identifying a ${computedSignal.setup.header.toLowerCase()}. Smart money is likely absorbing liquidity against retail flow.`
                                                    : `This setup is categorized as a ${computedSignal.setup.header.toLowerCase()}.`}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-2">
                                        {computedSignal.signalType !== 'NEUTRAL' && (
                                            <div className="col-span-2 mb-1">
                                                <span className="text-[10px] uppercase tracking-widest font-black text-slate-400 dark:text-slate-500">
                                                    Why this setup is valid:
                                                </span>
                                            </div>
                                        )}
                                        {thesisPoints.map((point: any, i: number) => (
                                            <div key={i} className="flex gap-2 items-start group">
                                                <div
                                                    className={`mt-1 shrink-0 w-1.5 h-1.5 rounded-full ${point.type === 'bull' ? 'bg-emerald-500' : point.type === 'bear' ? 'bg-rose-500' : point.type === 'risk' ? 'bg-amber-500' : 'bg-slate-400'}`}
                                                />
                                                <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 leading-snug group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors">
                                                    {point.text}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 2. Visual Range Meter V2 */}
                                <div className="bg-white/60 dark:bg-slate-800/60 rounded-2xl border border-white/40 dark:border-slate-700/40 p-5 shadow-sm relative ring-1 ring-white/60 dark:ring-slate-700/60">
                                    {/* Meter Header */}
                                    <div className="flex justify-between items-center mb-6">
                                        <span className="text-[9px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-widest">
                                            Range Dynamics
                                        </span>
                                        <div className="flex items-center gap-1">
                                            <Gauge size={12} className="text-slate-300 dark:text-slate-500" />
                                        </div>
                                    </div>

                                    <div className="relative h-12 w-full flex items-center">
                                        {/* Track */}
                                        <div className="absolute left-0 right-0 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden flex">
                                            <div className="h-full w-[33%] bg-emerald-500/10 dark:bg-emerald-400/10 border-r border-white/50 dark:border-slate-600" />
                                            <div className="h-full w-[34%] bg-amber-500/5 dark:bg-amber-400/5 border-r border-white/50 dark:border-slate-600" />
                                            <div className="h-full w-[33%] bg-rose-500/10 dark:bg-rose-400/10" />
                                        </div>

                                        {/* S1 Marker Tooltip */}
                                        <div className="absolute left-0 -bottom-8 flex flex-col items-center group">
                                            <div className="w-px h-3 bg-emerald-300 dark:bg-emerald-700 transition-all group-hover:h-4" />
                                            <div className="flex flex-col items-center mt-1">
                                                <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 mb-0.5">
                                                    S1
                                                </span>
                                                <div className="px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800 rounded shadow-sm">
                                                    <span className="text-[9px] font-black text-emerald-700 dark:text-emerald-300 premium-cell-mono block leading-none">
                                                        {levels.s1}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* R Marker Tooltip */}
                                        <div className="absolute right-0 -bottom-8 flex flex-col items-center group">
                                            <div className="w-px h-3 bg-rose-300 dark:bg-rose-700 transition-all group-hover:h-4" />
                                            <div className="flex flex-col items-center mt-1">
                                                <span className="text-[9px] font-bold text-rose-600 dark:text-rose-400 mb-0.5">
                                                    R
                                                </span>
                                                <div className="px-1.5 py-0.5 bg-rose-50 dark:bg-rose-900/30 border border-rose-100 dark:border-rose-800 rounded shadow-sm">
                                                    <span className="text-[9px] font-black text-rose-700 dark:text-rose-300 premium-cell-mono block leading-none">
                                                        {levels.r1}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Spot Price Puck */}
                                        <div
                                            className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-out z-20 group"
                                            style={{
                                                left: `${Math.min(95, Math.max(5, ((marketData.spotPrice - levels.s1) / (levels.r1 - levels.s1)) * 100))}%`,
                                            }}
                                        >
                                            <div className={`flex flex-col items-center cursor-help`}>
                                                {/* Puck Point */}
                                                <div
                                                    className={`relative w-4 h-4 rounded-full ${theme.bg} ring-4 ring-white dark:ring-slate-700 shadow-lg z-20 transition-transform group-hover:scale-110`}
                                                >
                                                    <div className="absolute inset-0 rounded-full bg-white opacity-30 animate-ping" />
                                                </div>

                                                {/* Always Visible Label */}
                                                <div className="absolute -top-9 px-2.5 py-1 bg-slate-800 dark:bg-slate-700 text-white text-[10px] font-bold rounded-lg shadow-xl whitespace-nowrap premium-cell-mono z-20 after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-4 after:border-transparent after:border-t-slate-800 dark:after:border-t-slate-700">
                                                    {marketData.spotPrice}
                                                </div>

                                                {/* RICH TOOLTIP (Hover) */}
                                                <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 z-30 w-48 pointer-events-none">
                                                    <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-100 dark:border-slate-700 p-3 ring-1 ring-slate-900/5 flex flex-col gap-2">
                                                        {/* Header */}
                                                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-2">
                                                            <span className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-wider">
                                                                Range Levels
                                                            </span>
                                                            <div className={`w-1.5 h-1.5 rounded-full ${theme.bg}`} />
                                                        </div>

                                                        {/* R1 Level (Outer Resistance) - Corrected Label */}
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] font-bold text-rose-400 uppercase">
                                                                Resistance 2
                                                            </span>
                                                            <span className="text-xs font-black text-rose-400 premium-cell-mono">
                                                                {levels.r2}
                                                            </span>
                                                        </div>

                                                        {/* R Level (Inner Resistance) */}
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] font-bold text-rose-600 dark:text-rose-500 uppercase">
                                                                Resistance
                                                            </span>
                                                            <span className="text-xs font-black text-rose-600 dark:text-rose-500 premium-cell-mono">
                                                                {levels.r1}
                                                            </span>
                                                        </div>

                                                        {/* Current Spot (Highlighted) */}
                                                        <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-700/50 p-1.5 rounded-lg border border-slate-100 dark:border-slate-700">
                                                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase">
                                                                LTP
                                                            </span>
                                                            <span className="text-xs font-black text-slate-800 dark:text-slate-100 premium-cell-mono">
                                                                {marketData.spotPrice}
                                                            </span>
                                                        </div>

                                                        {/* S Level (Inner Support) */}
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-500 uppercase">
                                                                Support
                                                            </span>
                                                            <span className="text-xs font-black text-emerald-600 dark:text-emerald-500 premium-cell-mono">
                                                                {levels.s1}
                                                            </span>
                                                        </div>

                                                        {/* S1 Level (Outer Support) - Corrected Label */}
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[10px] font-bold text-emerald-400 uppercase">
                                                                Support 2
                                                            </span>
                                                            <span className="text-xs font-black text-emerald-400 premium-cell-mono">
                                                                {levels.s2}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {/* Tooltip Arrow */}
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-8 border-transparent border-t-white/95 dark:border-t-slate-800/95 drop-shadow-sm" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 3. Bottom Metrics Row */}
                                <div className="grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-slate-100/50 dark:border-slate-700/50">
                                    <MiniMetric label="S" value={levels.s2} color="emerald" />
                                    <MiniMetric label="Shift" value={levels.shift} color="indigo" />
                                    <MiniMetric
                                        label="Variance"
                                        value={`${metrics.variance.toFixed(1)}%`}
                                        color={metrics.variance > 0 ? 'emerald' : 'rose'}
                                    />
                                    <MiniMetric label="VIX" value={metrics.vix.toFixed(2)} color="amber" />
                                    <MiniMetric label="R1" value={levels.r2} color="rose" />
                                </div>

                                {/* 4. Trend Factors Row (NEW) */}
                                {trendFactors && (
                                    <>
                                        <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-100/50 dark:border-slate-700/50">
                                            <TrendChip
                                                label="RSI Δ"
                                                value={trendFactors.rsiDivergence.toFixed(1)}
                                                trend={
                                                    trendFactors.rsiDivergence > 5
                                                        ? 'UP'
                                                        : trendFactors.rsiDivergence < -5
                                                          ? 'DOWN'
                                                          : 'SIDEWAYS'
                                                }
                                            />
                                            <TrendChip
                                                label="Shift"
                                                value={levels.shift}
                                                trend={trendFactors.shiftTrend}
                                            />
                                            <TrendChip
                                                label="Variance"
                                                value={`${metrics.variance.toFixed(1)}%`}
                                                trend={trendFactors.varianceTrend}
                                            />
                                            <TrendChip
                                                label="Count"
                                                value={
                                                    metrics.totalCount >= 0
                                                        ? `+${metrics.totalCount}`
                                                        : metrics.totalCount
                                                }
                                                trend={trendFactors.countTrend}
                                            />

                                            <TrendChip
                                                label="PCR"
                                                value={metrics.pcr.toFixed(2)}
                                                trend={trendFactors.pcrTrend}
                                            />
                                            <TrendChip
                                                label="VIX"
                                                value={metrics.vix.toFixed(2)}
                                                trend={trendFactors.vixTrend}
                                                invertColor={true} // VIX Down = Green, VIX Up = Red
                                            />
                                            <TrendChip
                                                label="Sup Trend"
                                                value={levels.s1}
                                                trend={trendFactors.supportTrend}
                                            />
                                            <TrendChip
                                                label="Res Trend"
                                                value={levels.r1}
                                                trend={trendFactors.resistanceTrend}
                                            />

                                            {(() => {
                                                const spotL = marketData?.spotPrice || 0
                                                const s1L = analysis?.supportStrike || 0
                                                const r1L = analysis?.resistanceStrike || 0
                                                const shiftL = analysis?.shift || 0
                                                let locTrend: 'UP' | 'DOWN' | 'SIDEWAYS' = 'SIDEWAYS'
                                                if (spotL <= s1L) locTrend = 'UP'
                                                else if (spotL >= r1L) locTrend = 'DOWN'
                                                else if (spotL > shiftL) locTrend = 'UP'
                                                else if (spotL < shiftL) locTrend = 'DOWN'

                                                return <TrendChip label="S/R Level" value={spotL} trend={locTrend} />
                                            })()}
                                            <TrendChip
                                                label="Old Shift"
                                                value={analysis?.shiftOld || analysis?.shift || 0}
                                                trend={trendFactors.shiftOldTrend}
                                            />
                                        </div>

                                        {/* Row 3: Strike Low Levels (Dedicated) */}
                                        {(() => {
                                            const sl = computedSignal.strikeLowData
                                            const hasData = sl.peLowsCount > 0 || sl.ceLowsCount > 0
                                            const isBullish = sl.netScore > 0
                                            const isBearish = sl.netScore < 0
                                            const chipBg = isBullish
                                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                                                : isBearish
                                                  ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800'
                                                  : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'
                                            const textColor = isBullish
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : isBearish
                                                  ? 'text-rose-600 dark:text-rose-400'
                                                  : 'text-slate-400 dark:text-slate-500'
                                            const signal = isBullish ? '▲' : isBearish ? '▼' : '—'
                                            return (
                                                <div
                                                    className={`mt-2 col-span-4 rounded-2xl border px-4 py-2.5 flex items-center justify-between gap-3 ${chipBg}`}
                                                >
                                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 shrink-0">
                                                        Strike Low Levels
                                                    </span>
                                                    {hasData ? (
                                                        <div className="flex items-center gap-3 flex-1 justify-end">
                                                            {/* PE Broken Count */}
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-[9px] font-bold text-emerald-500 uppercase">
                                                                    PE Broke
                                                                </span>
                                                                <span
                                                                    className={`text-xs font-black ${sl.peBrokenCount > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-300 dark:text-slate-600'}`}
                                                                >
                                                                    {sl.peBrokenCount}/{sl.peLowsCount}
                                                                </span>
                                                            </div>
                                                            {/* Divider */}
                                                            <div className="w-px h-4 bg-slate-200 dark:bg-slate-700" />
                                                            {/* CE Broken Count */}
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-[9px] font-bold text-rose-500 uppercase">
                                                                    CE Broke
                                                                </span>
                                                                <span
                                                                    className={`text-xs font-black ${sl.ceBrokenCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-300 dark:text-slate-600'}`}
                                                                >
                                                                    {sl.ceBrokenCount}/{sl.ceLowsCount}
                                                                </span>
                                                            </div>
                                                            {/* Status Signal */}
                                                            <div className="w-px h-4 bg-slate-200 dark:bg-slate-700" />
                                                            <span className={`text-sm font-black ${textColor}`}>
                                                                {signal}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-[10px] font-semibold text-slate-300 dark:text-slate-600 italic">
                                                            Select CE &amp; PE strikes in Inspection Parameters
                                                        </span>
                                                    )}
                                                </div>
                                            )
                                        })()}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}{' '}
            {/* Removed Local Popup - Now Handled Globally in <GlobalTradePopup /> */}
            {/* AI Insights Modal */}
            <AiInsightsModal
                isOpen={isAiModalOpen}
                onClose={closeAiModal}
                response={aiResponse}
                isLoading={isAiGenerating}
            />
        </>
    )
}

const MiniMetric = ({ label, value, color }: any) => {
    const colors: any = {
        emerald:
            'text-emerald-500 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800/50',
        rose: 'text-rose-500 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/40 border-rose-200 dark:border-rose-800/50',
        amber: 'text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/40 border-amber-200 dark:border-amber-800/50',
        indigo: 'text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/40 border-indigo-200 dark:border-indigo-800/50',
    }
    return (
        <div className={`flex flex-col items-center p-2 rounded-lg border ${colors[color] || colors.indigo}`}>
            <span className="text-[8px] font-black uppercase opacity-60">{label}</span>
            <span className="text-xs font-black premium-cell-mono">{value}</span>
        </div>
    )
}

const GexChip = ({ regime }: { regime: 'STABLE' | 'VOLATILE' | 'NEUTRAL' }) => {
    const config = {
        STABLE: {
            label: 'STABLE',
            color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/40',
            border: 'border-emerald-200 dark:border-emerald-800/50',
            tooltip: 'Positive GEX: Dealers damping volatility',
        },
        VOLATILE: {
            label: 'VOLATILE',
            color: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/40',
            border: 'border-rose-200 dark:border-rose-800/50',
            tooltip: 'Negative GEX: Dealers accelerating volatility',
        },
        NEUTRAL: {
            label: 'TRANSITION',
            color: 'text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60',
            border: 'border-slate-200 dark:border-slate-700/50',
            tooltip: 'Zero Gamma Flip: Regime change imminent',
        },
    }
    const { label, color, border, tooltip } = config[regime]

    return (
        <div
            className={`flex flex-col items-center p-1.5 rounded-lg border ${border} ${color} cursor-help`}
            title={tooltip}
        >
            <span className="text-[7px] font-bold uppercase opacity-70 tracking-wider">GEX Regime</span>
            <span className="text-[10px] font-black uppercase tracking-tighter">{label}</span>
        </div>
    )
}

const TrendChip = ({
    label,
    value,
    trend,
    invertColor = false,
}: {
    label: string
    value?: string | number
    trend: 'UP' | 'DOWN' | 'SIDEWAYS'
    invertColor?: boolean
}) => {
    const config = {
        UP: {
            icon: '▲',
            color: invertColor
                ? 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/40 border-rose-200 dark:border-rose-800/50' // inverted: UP = Red
                : 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800/50',
            border: invertColor
                ? 'border-rose-200 dark:border-rose-800/50'
                : 'border-emerald-200 dark:border-emerald-800/50',
        },
        DOWN: {
            icon: '▼',
            color: invertColor
                ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800/50' // inverted: DOWN = Green
                : 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/40 border-rose-200 dark:border-rose-800/50',
            border: invertColor
                ? 'border-emerald-200 dark:border-emerald-800/50'
                : 'border-rose-200 dark:border-rose-800/50',
        },
        SIDEWAYS: {
            icon: '◆',
            color: 'text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60',
            border: 'border-slate-200 dark:border-slate-700/50',
        },
    }
    const { icon, color, border } = config[trend]

    return (
        <div
            className={`flex flex-col items-center p-1.5 rounded-lg border ${border} ${color} transition-all duration-300 hover:scale-105`}
        >
            <span className="text-[7px] font-bold uppercase opacity-70 tracking-wider text-center">{label}</span>
            <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[10px] font-black premium-cell-mono">{value}</span>
                <span className="text-xs font-black">{icon}</span>
            </div>
        </div>
    )
}
